import { operantColor } from "@/lib/operants";
import { Hand, Target, RotateCw } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Resolve the best src for a card's photo:
 *   1. `card.image_path` present  → object-storage proxy (browser-cached 1h)
 *   2. `card.image_data_url` set  → legacy inline base64
 *   3. otherwise null → treat as text card
 * The proxy sends the auth cookie automatically since it's same-origin.
 */
function resolveImageSrc(card) {
  if (card?.image_path) return `${API}/cards/${card.id}/image`;
  if (card?.image_data_url) return card.image_data_url;
  return null;
}

/**
 * Universal stimulus card renderer.
 * Supports card_type: "photo" (image), "text" (large text — Wh/Fillin/Echoic),
 * "imitation" (label + instruction icon).
 * When `status` is set ("target" | "maintenance"), a prominent status badge is shown.
 * When card.stimulus_style === "illustration", a subtle "ILLUS" chip appears.
 */
export default function StimulusCard({
  card,
  size = "md",
  onClick,
  testId,
  className = "",
  selected = false,
  showSubtext = true,
  status = null,
}) {
  const c = operantColor(card.verbal_operant);
  const imgSrc = resolveImageSrc(card);
  const type = card.card_type || (imgSrc ? "photo" : "text");
  const sizes = {
    sm: { top: "h-32", pad: "p-3", label: "text-sm", big: "text-base" },
    md: { top: "h-40 sm:h-44", pad: "p-3 sm:p-4", label: "text-sm sm:text-base", big: "text-xl sm:text-2xl" },
    lg: { top: "h-64 sm:h-80", pad: "p-6", label: "text-2xl", big: "text-3xl sm:text-5xl" },
    xl: { top: "h-72 sm:h-[26rem]", pad: "p-8", label: "text-3xl sm:text-4xl", big: "text-4xl sm:text-6xl" },
  }[size];

  const subtext = card.prompt_text || card.pronunciation || "";

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`group relative text-left bg-white rounded-2xl overflow-hidden border-2 card-hover w-full cursor-pointer ${className}`}
      style={{
        borderColor: c.border,
        boxShadow: selected ? `0 0 0 4px ${c.ring}` : undefined,
      }}
    >
      {/* Top area: image OR big text */}
      <div className={`${sizes.top} w-full overflow-hidden relative`} style={{ background: c.bg }}>
        {type === "photo" && imgSrc ? (
          <img
            src={imgSrc}
            alt={card.label}
            className="w-full h-full object-cover"
            draggable={false}
            loading="lazy"
          />
        ) : type === "imitation" ? (
          <div className="w-full h-full flex flex-col items-center justify-center px-3 text-center">
            <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm">
              <Hand className="w-5 h-5" style={{ color: c.text }} />
            </div>
            <div className={`font-display font-extrabold ${sizes.big} leading-tight`} style={{ color: c.text }}>
              {card.label}
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center px-3 text-center">
            <div
              className={`font-display font-extrabold ${sizes.big} leading-[1.1] tracking-tight`}
              style={{ color: c.text }}
            >
              {card.label}
            </div>
          </div>
        )}
        {/* Status badge — clear at-a-glance mark of Target vs Maintenance */}
        {status && (
          <div
            className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow ${
              status === "target"
                ? "bg-emerald-600 text-white"
                : "bg-amber-500 text-white"
            }`}
          >
            {status === "target" ? <Target className="w-3 h-3" /> : <RotateCw className="w-3 h-3" />}
            {status === "target" ? "Target" : "Maint."}
          </div>
        )}
        {!status && card.category && (
          <div className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/85 backdrop-blur text-slate-600">
            {card.category}
          </div>
        )}
        {card.stimulus_style === "illustration" && (
          <div
            className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-500 text-white shadow"
            title="Illustration (non-real stimulus)"
          >
            ILLUS
          </div>
        )}
      </div>

      {/* Bottom label bar */}
      <div className={`${sizes.pad} bg-white`}>
        <div className="flex items-center justify-between gap-2">
          <div className={`font-display font-bold text-slate-900 ${sizes.label} truncate`}>
            {card.label}
          </div>
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ background: c.bg, color: c.text }}
          >
            {card.verbal_operant}
          </span>
        </div>
        {showSubtext && subtext && (
          <div className="mt-1.5 text-xs text-slate-500 line-clamp-2 italic">{subtext}</div>
        )}
      </div>
    </button>
  );
}
